import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Spinner, TextInput } from 'flowbite-react';
import * as wasm from 'sched';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { FiUser, FiUsers } from 'react-icons/fi';

interface AddTeamMemberFormProps {
  teamId: string;
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void> | void;
  onAdded?: () => void;
}

interface PersonSearchResult {
  id: string;
  name: string;
  avatar_url?: string | null;
}

interface TeamSearchResult {
  id: string;
  name: string;
}

// AI-generated, so possibly a mess
// I think there's a more general-purpose / generic search dropdown somewhere else here
const AddTeamMemberForm: React.FC<AddTeamMemberFormProps> = ({ teamId, onSubmit, onAdded }) => {
  const { safeData } = useContext(WorkspaceContext);
  const [personQuery, setPersonQuery] = useState('');
  const [teamQuery, setTeamQuery] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResult | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<TeamSearchResult | null>(null);
  const [personResults, setPersonResults] = useState<PersonSearchResult[]>([]);
  const [teamResults, setTeamResults] = useState<TeamSearchResult[]>([]);
  const [isSearchingPeople, setIsSearchingPeople] = useState(false);
  const [isSearchingTeams, setIsSearchingTeams] = useState(false);
  const [showPeopleDropdown, setShowPeopleDropdown] = useState(false);
  const [showTeamsDropdown, setShowTeamsDropdown] = useState(false);
  const peopleDropdownRef = useRef<HTMLDivElement>(null);
  const peopleInputRef = useRef<HTMLInputElement>(null);
  const teamsDropdownRef = useRef<HTMLDivElement>(null);
  const teamsInputRef = useRef<HTMLInputElement>(null);
  const [submittingPerson, setSubmittingPerson] = useState(false);
  const [submittingTeam, setSubmittingTeam] = useState(false);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (peopleDropdownRef.current && !peopleDropdownRef.current.contains(e.target as Node) &&
          peopleInputRef.current && !peopleInputRef.current.contains(e.target as Node)) {
        setShowPeopleDropdown(false);
      }
      if (teamsDropdownRef.current && !teamsDropdownRef.current.contains(e.target as Node) &&
          teamsInputRef.current && !teamsInputRef.current.contains(e.target as Node)) {
        setShowTeamsDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // People search
  useEffect(() => {
    const run = async () => {
      if (!safeData || !personQuery) {
        setPersonResults([]);
        setIsSearchingPeople(false);
        return;
      }
      setIsSearchingPeople(true);
      try {
        const resp = await wasm.search_people(safeData, personQuery, 6);
        const mapped: PersonSearchResult[] = resp.map((p) => {
          const person = p.person();
          return {
            id: person.id(),
            name: person.name(),
            avatar_url: person.avatar_url() ?? null,
          };
        });
        setPersonResults(mapped);
      } catch (err) {
        console.error('search_people failed', err);
        setPersonResults([]);
      } finally {
        setIsSearchingPeople(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [personQuery, safeData]);

  // Teams search
  useEffect(() => {
    const run = async () => {
      if (!safeData || !teamQuery) {
        setTeamResults([]);
        setIsSearchingTeams(false);
        return;
      }
      setIsSearchingTeams(true);
      try {
        const resp = await wasm.search_teams(safeData, teamQuery, 6);
        const mapped: TeamSearchResult[] = resp.map((t) => {
          const team = t.team();
          return {
            id: team.id(),
            name: team.name(),
          };
        });
        setTeamResults(mapped);
      } catch (err) {
        console.error('search_teams failed', err);
        setTeamResults([]);
      } finally {
        setIsSearchingTeams(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [teamQuery, safeData]);

  const addPerson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPerson) return;
    setSubmittingPerson(true);
    try {
      const command: wasm.DatabaseCommand = {
        type: 'SetTeamMembership',
        membership: {
          member: { Person: selectedPerson.id },
          team: teamId,
        },
        enabled: true,
      };
      await onSubmit(command);
      setSelectedPerson(null);
      setPersonQuery('');
      setShowPeopleDropdown(false);
      if (onAdded) onAdded();
    } catch (err) {
      console.error('Error adding person to team', err);
    } finally {
      setSubmittingPerson(false);
    }
  };

  const addTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam) return;
    setSubmittingTeam(true);
    try {
      const command: wasm.DatabaseCommand = {
        type: 'SetTeamMembership',
        membership: {
          member: { Team: selectedTeam.id },
          team: teamId,
        },
        enabled: true,
      };
      await onSubmit(command);
      setSelectedTeam(null);
      setTeamQuery('');
      setShowTeamsDropdown(false);
      if (onAdded) onAdded();
    } catch (err) {
      console.error('Error adding subteam', err);
    } finally {
      setSubmittingTeam(false);
    }
  };

  // We won't apply the command here; instead, expose a getCommand method via callback ref is complex.
  // To keep things straightforward in this codebase, handle applying in parent. So we export a helper below.

  return (
    <div className="space-y-3">
      {/* People */}
      <form onSubmit={addPerson} className="flex items-center gap-2">
        <div className="flex-1 relative">
          <div className="flex items-center gap-2">
            <FiUser className="text-cm-text-muted" />
            <TextInput
              ref={peopleInputRef}
              value={selectedPerson ? selectedPerson.name : personQuery}
              onChange={(e) => {
                setSelectedPerson(null);
                setPersonQuery(e.target.value);
                setShowPeopleDropdown(true);
              }}
              placeholder="Search people to add..."
              onFocus={() => setShowPeopleDropdown(true)}
              className="w-full"
            />
          </div>
          {showPeopleDropdown && (personQuery.length > 0 || isSearchingPeople) && (
            <div
              ref={peopleDropdownRef}
              className="absolute z-50 mt-1 w-full bg-cm-bg-surface border border-cm-border-subtle rounded-md shadow-lg max-h-60 overflow-auto"
            >
              {isSearchingPeople ? (
                <div className="px-3 py-2 text-sm text-cm-text-secondary flex items-center gap-2">
                  <Spinner size="sm" /> Searching...
                </div>
              ) : personResults.length > 0 ? (
                personResults.map((r) => (
                  <button
                    type="button"
                    key={r.id}
                    className="block w-full text-left px-3 py-2 hover:bg-cm-bg-hover"
                    onClick={() => {
                      setSelectedPerson(r);
                      setPersonQuery('');
                      setShowPeopleDropdown(false);
                    }}
                  >
                    <span className="text-sm">{r.name}</span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-cm-text-muted">No matches</div>
              )}
            </div>
          )}
        </div>
        <Button type="submit" size="sm" disabled={!selectedPerson || submittingPerson}>
          {submittingPerson ? 'Adding…' : 'Add'}
        </Button>
      </form>

      {/* Teams */}
      <form onSubmit={addTeam} className="flex items-center gap-2">
        <div className="flex-1 relative">
          <div className="flex items-center gap-2">
            <FiUsers className="text-cm-text-muted" />
            <TextInput
              ref={teamsInputRef}
              value={selectedTeam ? selectedTeam.name : teamQuery}
              onChange={(e) => {
                setSelectedTeam(null);
                setTeamQuery(e.target.value);
                setShowTeamsDropdown(true);
              }}
              placeholder="Search teams to add..."
              onFocus={() => setShowTeamsDropdown(true)}
              className="w-full"
            />
          </div>
          {showTeamsDropdown && (teamQuery.length > 0 || isSearchingTeams) && (
            <div
              ref={teamsDropdownRef}
              className="absolute z-50 mt-1 w-full bg-cm-bg-surface border border-cm-border-subtle rounded-md shadow-lg max-h-60 overflow-auto"
            >
              {isSearchingTeams ? (
                <div className="px-3 py-2 text-sm text-cm-text-secondary flex items-center gap-2">
                  <Spinner size="sm" /> Searching...
                </div>
              ) : teamResults.length > 0 ? (
                teamResults.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    className="block w-full text-left px-3 py-2 hover:bg-cm-bg-hover"
                    onClick={() => {
                      setSelectedTeam(t);
                      setTeamQuery('');
                      setShowTeamsDropdown(false);
                    }}
                  >
                    <span className="text-sm">{t.name}</span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-cm-text-muted">No matches</div>
              )}
            </div>
          )}
        </div>
        <Button type="submit" size="sm" disabled={!selectedTeam || submittingTeam}>
          {submittingTeam ? 'Adding…' : 'Add'}
        </Button>
      </form>
    </div>
  );
};

export default AddTeamMemberForm;
